import { useState, useMemo } from 'react'
import AppDropdown from '../../../../../components/AppDropdown'
import AppDateInput from '../../../../../components/AppDateInput'

const VALID_MONTHS = [1, 3, 6, 9, 12, 24]

function InfoBanner({ onDismiss }) {
  return (
    <div className="relative rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 text-xs leading-relaxed text-on-surface shadow-sm">
      <button
        type="button"
        onClick={onDismiss}
        className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-colors cursor-pointer"
        title="Dismiss"
      >
        <span className="material-symbols-outlined text-[16px]">close</span>
      </button>
      <div className="flex items-start gap-3 pr-6">
        <span className="material-symbols-outlined text-primary text-[20px] shrink-0 mt-0.5">info</span>
        <div>
          <span className="font-bold text-primary mr-1">Instructions:</span>
          Entering 0 for Roll No. and ID No. will automatically assign the next available No.
          For admissions in a single course, select the academic year and course name.
          For admissions into multiple courses, select No. of courses and then year/course name.
          You may also define course details along with subjects, batches and fee details in Scheme Master
          and simply click on the <strong>[Refresh from Scheme]</strong> button to automatically fill admission details.
        </div>
      </div>
    </div>
  )
}

function FormCard({ title, children, headerRight }) {
  return (
    <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between border-b border-outline-variant/15 pb-2.5">
        <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary">{title}</h3>
        {headerRight}
      </div>
      {children}
    </div>
  )
}

function FormInput({ label, value, onChange, type = 'text', placeholder, required, readOnly, disabled }) {
  if (type === 'date') {
    return (
      <div className="grid gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {label}{required ? <span className="text-error"> *</span> : null}
        </span>
        <AppDateInput
          value={value || ''}
          onChange={(val) => onChange && onChange(val)}
          placeholder={placeholder || 'DD-MM-YYYY'}
          disabled={disabled || readOnly}
        />
      </div>
    )
  }

  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
        {label}{required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value || ''}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={disabled}
        className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-2 text-xs text-on-surface outline-none transition-all focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
      />
    </label>
  )
}

function FormSelect({ label, value, onChange, options = [], required, disabled, placeholder = 'Select' }) {
  const normOptions = useMemo(() => {
    const seen = new Set()
    const result = []
    for (const opt of options || []) {
      if (opt === undefined || opt === null) continue
      const item = typeof opt === 'object' ? opt : { value: String(opt), label: String(opt) }
      const keyVal = String(item.value)
      if (!seen.has(keyVal)) {
        seen.add(keyVal)
        result.push(item)
      }
    }
    return result
  }, [options])

  return (
    <div className="grid gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
        {label}{required ? <span className="text-error"> *</span> : null}
      </span>
      <AppDropdown
        value={value || ''}
        onChange={(val) => onChange && onChange(val)}
        options={normOptions}
        disabled={disabled}
        placeholder={placeholder}
        triggerClassName="!h-9 !rounded-xl !border-outline-variant/30 !bg-surface-container-low !text-xs !text-on-surface hover:!bg-surface-container transition-all"
      />
    </div>
  )
}

function CheckboxGroup({ items = [], selected = [], onChange }) {
  const toggle = (item) => {
    if (selected.includes(item)) {
      onChange(selected.filter((s) => s !== item))
    } else {
      onChange([...selected, item])
    }
  }

  if (!items || items.length === 0) {
    return <p className="text-xs text-on-surface-variant/60 italic">No options available</p>
  }

  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      {items.map((item) => (
        <label key={item} className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={selected.includes(item)}
            onChange={() => toggle(item)}
            className="h-4 w-4 rounded-md border-outline-variant text-primary focus:ring-primary/20 cursor-pointer"
          />
          <span className="text-xs font-semibold text-on-surface">{item}</span>
        </label>
      ))}
    </div>
  )
}

const cleanSelectOptions = (list) => {
  const arr = Array.isArray(list) ? list : []
  const unique = Array.from(new Set(arr.filter((x) => x !== undefined && x !== null && String(x).trim() !== '')))
  if (unique.length === 0) return ['Select']
  if (!unique.includes('Select')) return ['Select', ...unique]
  return unique
}

export function CourseTab({ form = {}, patchForm = () => {}, readOnly = false, options = {} }) {
  const [infoBannerVisible, setInfoBannerVisible] = useState(true)

  const o = options || {}
  const COMPANY_OPTIONS = cleanSelectOptions(o.institutes)
  const BRANCH_OPTIONS = cleanSelectOptions(o.branches)
  const COUNSELLOR_OPTIONS = cleanSelectOptions(o.counsellors)
  const SOURCE_OPTIONS = cleanSelectOptions(o.sources)
  const SCHEME_OPTIONS = cleanSelectOptions(o.schemes)
  const COURSE_OPTIONS = cleanSelectOptions(o.courses)
  const ACADEMIC_YEARS = cleanSelectOptions(o.academic_years)
  const BATCH_OPTIONS = (o.batches && o.batches.length ? o.batches : []).filter((b) => b !== 'Select')
  const SUBJECT_OPTIONS = (o.subjects && o.subjects.length ? o.subjects : []).filter((s) => s !== 'Select')

  const [selectedBatches, setSelectedBatches] = useState(() => {
    return form.batch && form.batch !== 'Select' ? form.batch.split(',').map((b) => b.trim()).filter(Boolean) : []
  })
  const [selectedSubjects, setSelectedSubjects] = useState(() => {
    return []
  })

  const [courseRows, setCourseRows] = useState(() => {
    if (form.course && form.course !== 'Select') {
      return [{ id: 1, year: '2026-2027', course: form.course }]
    }
    return [{ id: 1, year: '2026-2027', course: 'Standard XII Science' }]
  })

  const addCourseRow = () => {
    setCourseRows((prev) => [...prev, { id: Date.now(), year: '2026-2027', course: 'Select' }])
  }

  const removeCourseRow = (id) => {
    const next = courseRows.filter((r) => r.id !== id)
    setCourseRows(next)
    const selected = next.map((r) => r.course).filter((c) => c && c !== 'Select')
    patchForm && patchForm('course', selected.length ? selected.join(', ') : 'Select')
  }

  const updateCourseRow = (id, field, value) => {
    const next = courseRows.map((r) => r.id === id ? { ...r, [field]: value } : r)
    setCourseRows(next)
    if (field === 'course') {
      const selected = next.map((r) => r.course).filter((c) => c && c !== 'Select')
      patchForm && patchForm('course', selected.length ? selected.join(', ') : 'Select')
    }
  }

  const handleAllSubjects = () => setSelectedSubjects([...SUBJECT_OPTIONS])
  const handleNoneSubjects = () => setSelectedSubjects([])

  return (
    <div className="space-y-6">
      {infoBannerVisible && (
        <InfoBanner onDismiss={() => setInfoBannerVisible(false)} />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <FormCard title="Admission Details">
          <div className="space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              <FormInput label="Roll No" value={form.rollNo || '0'} onChange={(v) => patchForm && patchForm('rollNo', v)} disabled={readOnly} />
              <FormInput label="ID#" value={form.idCardNo || '0'} onChange={(v) => patchForm && patchForm('idCardNo', v)} disabled={readOnly} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormInput label="Joined Date" value={form.admissionDate} onChange={(v) => patchForm && patchForm('admissionDate', v)} type="date" disabled={readOnly} />
              <div>
                <FormInput label="Valid Till" value={form.validTill} onChange={(v) => patchForm && patchForm('validTill', v)} type="date" disabled={readOnly} />
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className="text-[10px] text-on-surface-variant font-medium">Months:</span>
                  {VALID_MONTHS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        if (form.admissionDate) {
                          const d = new Date(form.admissionDate)
                          d.setMonth(d.getMonth() + m)
                          patchForm && patchForm('validTill', d.toISOString().slice(0, 10))
                        }
                      }}
                      className="h-5 rounded-lg border border-outline-variant/30 bg-surface-container-low px-1.5 text-[10px] font-bold text-on-surface-variant hover:bg-surface-container hover:text-primary transition-colors cursor-pointer"
                      title={`${m} months validity`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <FormSelect label="Company" value={form.institute} onChange={(v) => patchForm && patchForm('institute', v)} options={COMPANY_OPTIONS} required disabled={readOnly} />
            <FormSelect label="Branch" value={form.branch} onChange={(v) => patchForm && patchForm('branch', v)} options={BRANCH_OPTIONS} required disabled={readOnly} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormSelect label="Counsellor" value={form.counsellor} onChange={(v) => patchForm && patchForm('counsellor', v)} options={COUNSELLOR_OPTIONS} disabled={readOnly} />
              <FormSelect label="Source" value={form.source} onChange={(v) => patchForm && patchForm('source', v)} options={SOURCE_OPTIONS} disabled={readOnly} />
            </div>

            <FormSelect label="Scheme" value={form.scheme} onChange={(v) => patchForm && patchForm('scheme', v)} options={SCHEME_OPTIONS} disabled={readOnly} />

            <label className="flex items-center gap-2.5 cursor-pointer pt-2 select-none">
              <input
                type="checkbox"
                checked={form.allowMyInstitute || false}
                onChange={(e) => patchForm && patchForm('allowMyInstitute', e.target.checked)}
                disabled={readOnly}
                className="h-4 w-4 rounded-md border-outline-variant text-primary focus:ring-primary/20 cursor-pointer disabled:opacity-60"
              />
              <span className="text-xs font-semibold text-on-surface">Allow access to MyInstitute student portal</span>
            </label>
          </div>
        </FormCard>

        <FormCard
          title="Course Details"
          headerRight={
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-xl border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-bold text-primary hover:bg-primary/20 transition-all cursor-pointer shadow-xs"
            >
              <span className="material-symbols-outlined text-[14px]">sync</span>
              Refresh from Scheme
            </button>
          }
        >
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-xl border border-outline-variant/20 bg-surface-container-low/40">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low">
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider text-on-surface-variant">A. Year</th>
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider text-on-surface-variant">Course</th>
                    <th className="px-4 py-2.5 text-right font-bold uppercase tracking-wider text-on-surface-variant">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {courseRows.map((row) => (
                    <tr key={row.id} className="border-b border-outline-variant/10 last:border-0 bg-surface-container-lowest">
                      <td className="p-2 w-1/3">
                        <AppDropdown
                          value={row.year}
                          onChange={(v) => updateCourseRow(row.id, 'year', v)}
                          options={ACADEMIC_YEARS.map((y) => ({ value: y, label: y }))}
                          disabled={readOnly}
                          triggerClassName="!h-8 !rounded-lg !border-outline-variant/20 !text-xs"
                        />
                      </td>
                      <td className="p-2">
                        <AppDropdown
                          value={row.course}
                          onChange={(v) => updateCourseRow(row.id, 'course', v)}
                          options={COURSE_OPTIONS.map((c) => ({ value: c, label: c }))}
                          disabled={readOnly}
                          triggerClassName="!h-8 !rounded-lg !border-outline-variant/20 !text-xs"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeCourseRow(row.id)}
                          disabled={readOnly || courseRows.length <= 1}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-error hover:bg-error-container/40 transition-colors disabled:opacity-30 cursor-pointer"
                          title="Remove Course"
                        >
                          <span className="material-symbols-outlined text-[16px]">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!readOnly && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={addCourseRow}
                  className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline cursor-pointer"
                >
                  <span className="material-symbols-outlined text-[15px]">add</span>
                  Add Another Course
                </button>
              </div>
            )}
          </div>
        </FormCard>
      </div>

      <FormCard title="Batches">
        <CheckboxGroup
          items={BATCH_OPTIONS}
          selected={selectedBatches}
          onChange={(next) => {
            setSelectedBatches(next)
            patchForm && patchForm('batch', next.length ? next.join(', ') : 'Select')
          }}
        />
      </FormCard>

      <FormCard
        title="Subjects"
        headerRight={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAllSubjects}
              disabled={readOnly}
              className="rounded-lg border border-outline-variant/30 bg-surface-container-low px-2.5 py-1 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition-colors cursor-pointer"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleNoneSubjects}
              disabled={readOnly}
              className="rounded-lg border border-outline-variant/30 bg-surface-container-low px-2.5 py-1 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition-colors cursor-pointer"
            >
              Clear All
            </button>
          </div>
        }
      >
        <CheckboxGroup
          items={SUBJECT_OPTIONS}
          selected={selectedSubjects}
          onChange={setSelectedSubjects}
        />
      </FormCard>
    </div>
  )
}
